import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMenu, FiX, FiUser, FiLogOut } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    closeMenu();
  };

  const scrollToSection = (sectionId) => {
    // Wenn wir nicht auf der Homepage sind, zuerst zur Homepage navigieren
    if (location.pathname !== '/') {
      navigate('/');
      // Kurz warten, bis die Navigation abgeschlossen ist
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // Direkt zur Sektion scrollen wenn wir schon auf der Homepage sind
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    closeMenu();
  };

  const menuItems = [
    { id: 'vision', label: 'Vision' },
    { id: 'mission', label: 'Mission' },
    { id: 'solutions', label: 'Lösungen' },
    { id: 'contact', label: 'Kontakt' }
  ];

  return (
    <motion.header
      className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Link to="/" onClick={closeMenu}>
              <img
                src="https://github.com/mike3011/Bilder/blob/main/3.png?raw=true"
                alt="K³ Logo"
                className="h-10 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {menuItems.map((item, index) => (
              <button
                key={index}
                onClick={() => scrollToSection(item.id)}
                className="text-gray-700 hover:text-azure-600 transition-colors cursor-pointer"
              >
                {item.label}
              </button>
            ))}
            
            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <Link
                  to="/dashboard"
                  className="flex items-center text-gray-700 hover:text-azure-600 transition-colors"
                >
                  <SafeIcon icon={FiUser} className="text-lg mr-2" />
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center text-gray-700 hover:text-azure-600 transition-colors"
                >
                  <SafeIcon icon={FiLogOut} className="text-lg mr-2" />
                  Abmelden
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-azure-600 transition-colors"
                >
                  Anmelden
                </Link>
                <a
                  href="https://cal.meetergo.com/mike-trepte/15-min-telefonat-kostenfrei"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-azure-600 text-white px-6 py-2 rounded-lg hover:bg-azure-700 transition-colors"
                >
                  Kostenloses Gespräch
                </a>
              </div>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="md:hidden p-2 text-gray-700 hover:text-azure-600 transition-colors"
            aria-label="Menü öffnen"
          >
            <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="text-2xl" />
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-white border-t border-gray-100 shadow-lg"
          >
            <nav className="container mx-auto px-6 py-4">
              <div className="flex flex-col space-y-4">
                {menuItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <button
                      onClick={() => scrollToSection(item.id)}
                      className="text-gray-700 hover:text-azure-600 transition-colors py-2 text-lg font-medium block w-full text-left"
                    >
                      {item.label}
                    </button>
                  </motion.div>
                ))}
                
                {isAuthenticated ? (
                  <>
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.4 }}
                    >
                      <Link
                        to="/dashboard"
                        onClick={closeMenu}
                        className="flex items-center text-gray-700 hover:text-azure-600 transition-colors py-2 text-lg font-medium"
                      >
                        <SafeIcon icon={FiUser} className="text-lg mr-2" />
                        Dashboard
                      </Link>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.5 }}
                    >
                      <button
                        onClick={handleLogout}
                        className="flex items-center text-gray-700 hover:text-azure-600 transition-colors py-2 text-lg font-medium w-full text-left"
                      >
                        <SafeIcon icon={FiLogOut} className="text-lg mr-2" />
                        Abmelden
                      </button>
                    </motion.div>
                  </>
                ) : (
                  <>
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.4 }}
                    >
                      <Link
                        to="/login"
                        onClick={closeMenu}
                        className="text-gray-700 hover:text-azure-600 transition-colors py-2 text-lg font-medium block w-full text-left"
                      >
                        Anmelden
                      </Link>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.5 }}
                    >
                      <a
                        href="https://cal.meetergo.com/mike-trepte/15-min-telefonat-kostenfrei"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={closeMenu}
                        className="bg-azure-600 text-white px-6 py-3 rounded-lg hover:bg-azure-700 transition-colors text-center font-semibold block"
                      >
                        Kostenloses Gespräch
                      </a>
                    </motion.div>
                  </>
                )}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;